import { useState } from "react";
import { useDebounceValue } from "usehooks-ts";
import { InView } from "react-intersection-observer";
import { Loader2 } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  SearchInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
  VisuallyHidden,
} from "web-design-system";
import { handleError } from "web-core/utils";
import {
  useFunds,
  useInfiniteSwitchCandidates,
  useUpdatePortfolioReviewLine,
} from "creso-core/hooks";
import { Fund, PortfolioReviewLine, SwitchCandidate } from "creso-core/types";
import {
  convertToCurrency,
  formatAsCrore,
  formatAsPercentage,
} from "creso-core/utils";
import { EditColumnsMenu } from "./edit-columns-menu";
import { FundCell, QuartileBadge } from "./fund-badges";
import { SWITCH_COLUMNS, SwitchColumnKey } from "./holding-columns";

export function SwitchFundDialog({
  reviewId,
  line,
  open,
  onOpenChange,
}: {
  reviewId: number;
  line: PortfolioReviewLine;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-5xl flex-col gap-6">
        <DialogHeader>
          <VisuallyHidden>
            <DialogTitle>Switch to a new fund</DialogTitle>
          </VisuallyHidden>
          <span className="text-lg font-semibold">Switch to a new fund</span>
          <DialogDescription className="sr-only">
            Pick a fund from the same category to switch into.
          </DialogDescription>
        </DialogHeader>
        {/* Mounted only while open, so search and page reset on reopen. */}
        {open ? (
          <SwitchCandidates
            reviewId={reviewId}
            line={line}
            onSwitched={() => onOpenChange(false)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function SwitchCandidates({
  reviewId,
  line,
  onSwitched,
}: {
  reviewId: number;
  line: PortfolioReviewLine;
  onSwitched: () => void;
}) {
  const [search, setSearch] = useState("");
  const [q] = useDebounceValue(search, 400);
  const [visibleColumns, setVisibleColumns] = useState<SwitchColumnKey[]>(
    SWITCH_COLUMNS.filter((c) => c.group === "visible").map((c) => c.key)
  );
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useInfiniteSwitchCandidates({ reviewId, lineId: line.id, q });
  const pages = data?.pages ?? [];
  const {
    mutate: updateLine,
    isPending,
    variables,
  } = useUpdatePortfolioReviewLine();

  // Candidates are always drawn from the holding's own category.
  const category = line.target_category || line.source_category;
  const columns = SWITCH_COLUMNS.filter((c) => visibleColumns.includes(c.key));

  const pick = (candidate: SwitchCandidate) => {
    if (!candidate.fund_scheme_id) return;
    updateLine(
      {
        reviewId,
        lineId: line.id,
        action: "SWITCH",
        target_fund_scheme: candidate.fund_scheme_id,
      },
      {
        onSuccess: onSwitched,
        onError: (error) => handleError(error, "Could not switch the fund"),
      }
    );
  };

  return (
    <div className="flex min-h-0 flex-col gap-6">
      <div className="flex flex-col gap-2 rounded-lg border border-border p-4">
        <span className="text-xs font-semibold text-muted-foreground">
          Switch from
        </span>
        <div className="flex items-center justify-between gap-4">
          <FundCell
            name={line.source_scheme_name}
            category={line.source_category}
            amcCode={line.amc_morningstar_code}
          />
          <span className="text-lg font-semibold">
            {convertToCurrency(line.current_value)}
          </span>
        </div>
      </div>

      <div className="flex min-h-0 flex-col gap-4">
        {/* Favourites have no API yet, so only the full list is offered. */}
        <Tabs value="all">
          <TabsList>
            <TabsTrigger value="all">All funds</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex flex-row items-center gap-4">
          <div className="w-85">
            <SearchInput
              placeholder="Search for funds"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Select value={category} disabled>
            <SelectTrigger className="w-auto min-w-46" aria-label="Category">
              <SelectValue>Category: {categoryLabel(category)}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={category}>
                {categoryLabel(category)}
              </SelectItem>
            </SelectContent>
          </Select>
          <div className="ml-auto">
            <EditColumnsMenu
              columns={SWITCH_COLUMNS}
              visible={visibleColumns}
              onChange={setVisibleColumns}
            />
          </div>
        </div>

        <div className="min-h-0 overflow-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-card">
              <tr>
                <th className="p-4 text-left font-semibold">Fund Name</th>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className="whitespace-nowrap p-4 text-right font-semibold"
                  >
                    {column.label}
                  </th>
                ))}
                <th className="p-4" />
              </tr>
            </thead>
            {pages.map((page, index) => (
              <CandidatePage
                key={index}
                candidates={page.results}
                columns={columns.map((c) => c.key)}
                currentTargetId={line.target_fund_scheme_id}
                pickingId={isPending ? variables?.target_fund_scheme : null}
                disabled={isPending}
                onPick={pick}
              />
            ))}
          </table>
          {isLoading ? (
            <div className="flex justify-center p-6">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
            </div>
          ) : !pages[0]?.results.length ? (
            <p className="p-6 text-center text-sm text-muted-foreground">
              No eligible funds in this category
            </p>
          ) : null}
          {hasNextPage ? (
            <InView
              as="div"
              onChange={(inView) => {
                if (inView && !isFetchingNextPage) fetchNextPage();
              }}
              className="flex justify-center p-4"
            >
              <Loader2 className="size-4 animate-spin text-muted-foreground" />
            </InView>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// One page of candidates, so AUM and returns are looked up once per page.
function CandidatePage({
  candidates,
  columns,
  currentTargetId,
  pickingId,
  disabled,
  onPick,
}: {
  candidates: SwitchCandidate[];
  columns: SwitchColumnKey[];
  currentTargetId: number | null;
  pickingId?: number | null;
  disabled: boolean;
  onPick: (candidate: SwitchCandidate) => void;
}) {
  const fundsById = useCandidateFunds(candidates);
  return (
    <tbody>
      {candidates.map((candidate) => (
        <CandidateRow
          key={candidate.isin}
          candidate={candidate}
          fund={fundsById.get(candidate.fund_id)}
          columns={columns}
          isCurrent={candidate.fund_scheme_id === currentTargetId}
          isPicking={
            pickingId != null && pickingId === candidate.fund_scheme_id
          }
          disabled={disabled}
          onPick={() => onPick(candidate)}
        />
      ))}
    </tbody>
  );
}

function CandidateRow({
  candidate,
  fund,
  columns,
  isCurrent,
  isPicking,
  disabled,
  onPick,
}: {
  candidate: SwitchCandidate;
  fund?: Fund;
  columns: SwitchColumnKey[];
  isCurrent: boolean;
  isPicking: boolean;
  disabled: boolean;
  onPick: () => void;
}) {
  const cells: Record<SwitchColumnKey, React.ReactNode> = {
    rank: candidate.category_rank ?? "-",
    aum: fund ? formatAsCrore(fund.assets_under_management, 0) : "-",
    return_1y: formatAsPercentage(fund?.performance?.return_1_year, "-"),
    return_3y: formatAsPercentage(fund?.performance?.return_3_year, "-"),
    quartile: <QuartileBadge quartile={candidate.quartile} />,
  };

  return (
    <tr className="border-t border-border">
      <td className="px-4 py-3">
        <FundCell
          name={candidate.scheme_name}
          category={candidate.category}
          amcCode={candidate.amc_morningstar_code}
        />
      </td>
      {columns.map((key) => (
        <td key={key} className="whitespace-nowrap px-4 py-3 text-right">
          {cells[key]}
        </td>
      ))}
      <td className="px-4 py-3 text-right">
        <Button
          variant="outline"
          className="w-29"
          disabled={isCurrent || disabled || !candidate.fund_scheme_id}
          onClick={onPick}
        >
          {isPicking ? (
            <Loader2 className="animate-spin" />
          ) : isCurrent ? (
            "Selected"
          ) : (
            "Recommend"
          )}
        </Button>
      </td>
    </tr>
  );
}

// AUM and returns are not on the candidates payload yet, so each page's funds
// are looked up from the catalogue in one request.
function useCandidateFunds(candidates: SwitchCandidate[]) {
  const fundIds = candidates.map((c) => c.fund_id);
  const { data } = useFunds(
    {
      pageNo: 1,
      pageSize: Math.max(fundIds.length, 1),
      filters: { fund_id_in: fundIds },
    },
    { enabled: fundIds.length > 0 }
  );
  return new Map((data?.results ?? []).map((fund) => [fund.fund_id, fund]));
}

// "Equity - Small Cap Fund" reads as "Small Cap Fund" next to the label.
function categoryLabel(category: string) {
  return category.split(" - ").pop() ?? category;
}
